import React, { useState } from 'react';
import { benchmarkMargin } from '../data/categoryBenchmarks';
import Tooltip from './Tooltip';

const formatMoney = (val, sym = '$') => {
  const sign = val < 0 ? '-' : '';
  return `${sign}${sym}${Math.abs(val).toFixed(2)}`;
};

export default function SnapshotCard({
  result,
  onCopy,
  onShareLink,
  shareStatus,
  copyStatus,
  settings,
  onClearModifier,
}) {
  const [showWorking, setShowWorking] = useState(false);

  if (!result) {
    return (
      <div className="sketch-card h-full flex flex-col">
        <h2 className="font-display text-2xl text-ink mb-5">📊 Financial Snapshot</h2>
        <div className="font-note text-xl text-ink/68 text-center py-12 border-2 border-dashed border-ink/30 rounded-sketch">
          ~ fill in your numbers and tap Calculate ~
        </div>
      </div>
    );
  }

  const sym = result.currencySymbol || '$';

  const rows = [
    { label: 'Listing Fee', emoji: '🧾', value: result.listingFee, color: 'bg-amber-400',
      tip: '$0.20 per item Etsy charges to keep your listing live for 4 months.' },
    { label: 'Transaction Fee', emoji: '💳', value: result.transactionFee, color: 'bg-sky-400',
      tip: "Etsy's 6.5% commission on the total order amount, including shipping you charge." },
    { label: 'Processing Fee', emoji: '🔁', value: result.processingFee, color: 'bg-rose-400',
      tip: 'Payment processing, varies by country. US: 3% + $0.25.' },
    ...(result.regulatoryFee > 0 ? [{
      label: 'Regulatory Fee', emoji: '🏛️', value: result.regulatoryFee, color: 'bg-purple-400',
      tip: 'Digital services tax pass-through in some countries (UK, France, Italy, etc.).'
    }] : []),
    ...(result.offsiteFee > 0 ? [{
      label: 'Offsite Ads', emoji: '📡', value: result.offsiteFee, color: 'bg-fuchsia-400',
      tip: '12-15% of order total, only when sale comes via an Etsy ad. Capped at $100.'
    }] : []),
    { label: 'Total Revenue', emoji: '📊', value: result.revenue, color: 'bg-emerald-400', divider: true },
    { label: 'Total Cost + Fees', emoji: '💸', value: result.totalCost, color: 'bg-orange-400' },
    {
      label: 'Net Profit',
      emoji: result.profit >= 0 ? '🎉' : '⚠️',
      value: result.profit,
      color: result.profit >= 0 ? 'bg-emerald-400' : 'bg-rose-400',
      bold: result.taxRate <= 0,   // when tax is on, after-tax becomes the headline
    },
    ...(result.taxRate > 0 && result.profit > 0 ? [
      {
        label: `Tax set-aside (${result.taxRate}%)`,
        emoji: '🏦',
        value: -result.taxAmount,    // shown as expense
        color: 'bg-purple-400',
        tip: `What you should set aside for self-employment / income taxes.`,
      },
      {
        label: 'You Keep',
        emoji: '💰',
        value: result.afterTaxProfit,
        color: 'bg-emerald-400',
        bold: true,
      },
    ] : []),
  ];

  return (
    <div className="sketch-card h-full flex flex-col">
      <div className="flex items-center justify-between mb-5 gap-2 flex-wrap">
        <h2 className="font-display text-2xl text-ink">📊 Financial Snapshot</h2>
        <div className="flex items-center gap-3">
          {(copyStatus || shareStatus) && (
            <span className="font-note text-note text-primary">
              {shareStatus || copyStatus}
            </span>
          )}
          <button
            onClick={onCopy}
            className="font-note text-note text-ink/68 hover:text-primary transition-colors"
            title="Copy summary text to clipboard"
          >
            📋 copy
          </button>
          {onShareLink && (
            <button
              onClick={onShareLink}
              title="Copy a link that opens this exact view"
              style={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 600,
                fontSize: '0.93rem',
                letterSpacing: '0.02em',
                padding: '0.25rem 0.7rem',
                color: '#fefefe',
                backgroundColor: '#1e3a8a',
                border: '1px solid rgba(26, 26, 46, 0.1)',
                borderRadius: '999px',
                boxShadow: '2px 2px 0 rgba(26,26,46,0.18)',
                cursor: 'pointer',
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
                transition: 'all 0.1s',
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'translateY(1px)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(26,26,46,0.04), 0 2px 4px rgba(26,26,46,0.04)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(26,26,46,0.04), 0 2px 4px rgba(26,26,46,0.04)';
              }}
            >
              🔗 share
            </button>
          )}
        </div>
      </div>

      {/* Active modifiers — chips you can click off to remove from this calculation */}
      {(() => {
        const mods = [];
        if (result.taxRate > 0) {
          mods.push({
            name: 'tax',
            label: `🏦 Tax ${result.taxRate}%`,
            tone: { bg: '#f3e8ff', border: '#c084fc', text: '#7e22ce' },
          });
        }
        if (settings?.offsiteEnabled) {
          const rate = settings.offsiteRate || 0.15;
          mods.push({
            name: 'offsite',
            label: `📡 Offsite ${(rate * 100).toFixed(0)}%`,
            tone: { bg: '#fae8ff', border: '#e879f9', text: '#a21caf' },
          });
        }
        if (settings?.orderType === 'bulk') {
          mods.push({
            name: 'bulk',
            label: '📦 Bulk order',
            tone: { bg: '#dbeafe', border: '#60a5fa', text: '#1d4ed8' },
          });
        }
        if (mods.length === 0) return null;
        return (
          <div className="flex flex-wrap gap-1.5 mb-4 -mt-2">
            <span
              className="font-body text-xs text-ink/72"
              style={{ alignSelf: 'center', fontWeight: 600, marginRight: '0.15rem' }}
            >
              Active:
            </span>
            {mods.map(m => (
              <button
                key={m.name}
                onClick={() => onClearModifier && onClearModifier(m.name)}
                title={`Remove ${m.label} from this calculation`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  padding: '0.2rem 0.55rem',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.86rem',
                  fontWeight: 600,
                  background: m.tone.bg,
                  border: `1px solid ${m.tone.border}`,
                  borderRadius: '999px',
                  color: m.tone.text,
                  cursor: onClearModifier ? 'pointer' : 'default',
                  letterSpacing: '0.01em',
                  transition: 'all 0.12s',
                }}
                onMouseEnter={(e) => {
                  if (!onClearModifier) return;
                  e.currentTarget.style.background = m.tone.border;
                  e.currentTarget.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = m.tone.bg;
                  e.currentTarget.style.color = m.tone.text;
                }}
              >
                {m.label}
                {onClearModifier && (
                  <span aria-hidden="true" style={{ fontSize: '1.02rem', lineHeight: 1, opacity: 0.7 }}>
                    ×
                  </span>
                )}
              </button>
            ))}
          </div>
        );
      })()}

      {/* HEADLINE: answer to "How much does Etsy take?" — the #1 question */}
      {result.revenue > 0 && (
        <div
          className="flex items-baseline justify-between gap-3 mb-4 px-3 py-2.5"
          style={{
            background: 'rgba(30, 58, 138, 0.04)',
            border: '1px solid rgba(30, 58, 138, 0.15)',
            borderRadius: '0.55rem',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <p className="font-body text-xs text-ink/78" style={{ fontWeight: 600, letterSpacing: '0.04em' }}>
              ETSY KEEPS
            </p>
            <p
              className="font-numbers"
              style={{
                fontSize: '1.76rem',
                fontWeight: 700,
                color: result.effectiveFeeRate >= 25 ? '#9f1239' : result.effectiveFeeRate >= 15 ? '#b45309' : '#1e3a8a',
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.02em',
                lineHeight: 1.05,
              }}
            >
              {result.effectiveFeeRate}%
            </p>
          </div>
          <div style={{ flex: 1, textAlign: 'right' }}>
            <p className="font-body text-xs text-ink/78" style={{ fontWeight: 600, letterSpacing: '0.04em' }}>
              YOU KEEP
            </p>
            <p
              className="font-numbers"
              style={{
                fontSize: '1.76rem',
                fontWeight: 700,
                color: result.profit >= 0 ? '#047857' : '#9f1239',
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.02em',
                lineHeight: 1.05,
              }}
            >
              {(100 - result.effectiveFeeRate).toFixed(1)}%
            </p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {rows.map(({ label, emoji, value, color, bold, tip, divider }) => {
          const denom = result.revenue || 1;
          const widthPct = Math.min((Math.abs(value) / denom) * 100, 100);
          return (
            <React.Fragment key={label}>
              {divider && <div className="border-t-2 border-dashed border-ink/30 mt-2 mb-2" />}
              <div>
                <div className="flex justify-between items-baseline gap-2">
                  <span className="font-sketch text-body flex items-center gap-1">
                    <span>{emoji} {label}</span>
                    {tip && <Tooltip>{tip}</Tooltip>}
                  </span>
                  <span
                    className={`font-numbers ${bold ? 'text-3xl' : 'text-2xl'}`}
                    style={{
                      fontWeight: bold ? 700 : 600,
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '-0.01em',
                      color: (() => {
                        if (label === 'Net Profit')        return value >= 0 ? '#047857' : '#9f1239';
                        if (label === 'You Keep')          return value >= 0 ? '#047857' : '#9f1239';
                        if (label.startsWith('Tax set-as')) return '#7e22ce';
                        if (label === 'Total Revenue')     return '#047857';
                        if (label === 'Total Cost + Fees') return '#c2410c';
                        return '#1a1a2e';
                      })(),
                    }}
                  >
                    {formatMoney(value, sym)}
                  </span>
                </div>
                <div className="relative w-full h-4 mt-1.5 border-2 border-ink rounded-full overflow-hidden" style={{ background: '#ffffff' }}>
                  <div
                    className={`absolute top-0 left-0 h-full ${color}`}
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Quick stats — 4 if hourly rate available, else 3 */}
      <div
        className={`mt-5 pt-4 border-t-2 border-dashed border-ink/30 grid gap-3 text-center ${
          result.profitPerHour !== null && Number.isFinite(result.profitPerHour)
            ? 'grid-cols-2 sm:grid-cols-4'
            : 'grid-cols-3'
        }`}
      >
        <div>
          <p className="font-note text-note text-ink/75">Margin</p>
          <p className={`font-numbers text-xl ${result.margin >= 0 ? 'text-primary' : 'text-alert'}`} style={{ fontWeight: 700 }}>
            {result.margin}%
          </p>
          {(() => {
            const bench = benchmarkMargin(result.margin, settings?.productCategory);
            if (!bench) return null;
            const tone = bench.tier === 'top'  ? { bg: '#d1fae5', text: '#065f46', border: '#047857' }
                       : bench.tier === 'good' ? { bg: '#ecfccb', text: '#3f6212', border: '#65a30d' }
                       : bench.tier === 'okay' ? { bg: '#fef3c7', text: '#92400e', border: '#b45309' }
                       : { bg: '#ffe4e6', text: '#9f1239', border: '#be123c' };
            return (
              <p
                title={`Industry estimate · median margin for ${bench.label} sellers is ${bench.median}%`}
                style={{
                  display: 'inline-block',
                  marginTop: '4px',
                  padding: '0.1rem 0.45rem',
                  background: tone.bg,
                  border: `1px solid ${tone.border}`,
                  borderRadius: '999px',
                  color: tone.text,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.86rem',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {bench.tier === 'top' ? '★ ' : ''}{bench.message}
              </p>
            );
          })()}
        </div>
        <div>
          <p className="font-note text-note text-ink/75">Etsy Takes</p>
          <p className="font-numbers text-xl text-ink" style={{ fontWeight: 700 }}>{result.effectiveFeeRate}%</p>
        </div>
        <div>
          <p className="font-note text-note text-ink/75">Markup</p>
          <p className="font-numbers text-xl text-ink" style={{ fontWeight: 700 }}>{result.markup}×</p>
        </div>
        {result.profitPerHour !== null && Number.isFinite(result.profitPerHour) && (
          <div title={`Total labor: ${result.totalLaborHours} hr at ${result.currencySymbol}${result.profitPerHour}/hr`}>
            <p className="font-note text-note text-ink/75">Per Hour</p>
            <p
              className="font-numbers text-xl"
              style={{
                fontWeight: 700,
                color: result.profitPerHour >= 25 ? '#047857' :
                       result.profitPerHour >= 12 ? '#b45309' :
                       '#9f1239',
              }}
            >
              {result.currencySymbol}{result.profitPerHour}
            </p>
          </div>
        )}
      </div>

      {/* Hourly rate context line — only shown when labor data present */}
      {result.profitPerHour !== null && Number.isFinite(result.profitPerHour) && (
        <p className="font-body text-xs text-ink/72 mt-3 text-center">
          You earn{' '}
          <span style={{
            fontWeight: 700,
            color: result.profitPerHour >= 25 ? '#047857' :
                   result.profitPerHour >= 12 ? '#b45309' :
                   '#9f1239',
          }}>
            {result.currencySymbol}{result.profitPerHour}/hour
          </span>{' '}
          on this — total {result.totalLaborHours} hr of work.
        </p>
      )}

      {result.returnRateApplied && (
        <p className="font-note text-note text-ink/68 mt-3 text-center">
          ~ profit adjusted for return rate ~
        </p>
      )}

      {/* Show working — expandable formula breakdown for trust + transparency */}
      <div className="mt-4 pt-3" style={{ borderTop: '1px solid rgba(26,26,46,0.08)' }}>
        <button
          onClick={() => setShowWorking(v => !v)}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#1e3a8a',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.86rem',
            fontWeight: 600,
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3rem',
            opacity: 0.75,
            transition: 'opacity 0.12s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.75'}
        >
          <span style={{ fontSize: '0.86rem' }}>{showWorking ? '▼' : '▶'}</span>
          {showWorking ? 'Hide the math' : 'Show the math'}
        </button>
        {showWorking && (
          <div className="mt-3" style={{
            padding: '0.75rem 0.85rem',
            background: 'rgba(26, 26, 46, 0.02)',
            border: '1px solid rgba(26, 26, 46, 0.08)',
            borderRadius: '0.5rem',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.86rem',
            lineHeight: 1.6,
            color: '#1a1a2e',
          }}>
            <ShowWorking result={result} sym={sym} />
          </div>
        )}
      </div>
    </div>
  );
}

// (InlineProfitFinder helper has been promoted to its own component —
//  see ProfitPriceFinderBar.js, rendered as a standalone bar in App.js)

// ─── Helper: line-by-line math formula breakdown ─────────────────────
function ShowWorking({ result, sym }) {
  const fmt = (v) => `${sym}${Math.abs(v).toFixed(2)}`;
  const lines = [];

  // Revenue
  lines.push({ kind: 'header', text: 'Revenue' });
  lines.push({ kind: 'calc',
    text: `Price × Quantity = ${fmt(result.revenue / (result.quantity || 1))} × ${result.quantity || 1} = ${fmt(result.revenue)}` });
  if (result.discountAmount > 0) {
    lines.push({ kind: 'calc', text: `(after ${fmt(result.discountAmount)} discount)` });
  }

  // Fees
  lines.push({ kind: 'header', text: 'Etsy fees' });
  if (result.listingFee > 0) {
    lines.push({ kind: 'calc',
      text: `Listing: ${sym}0.20 × ${result.quantity || 1} = ${fmt(result.listingFee)}` });
  }
  if (result.transactionFee > 0) {
    lines.push({ kind: 'calc',
      text: `Transaction: 6.5% × ${fmt(result.revenue)} = ${fmt(result.transactionFee)}` });
  }
  if (result.processingFee > 0) {
    lines.push({ kind: 'calc',
      text: `Processing: country-specific % + flat = ${fmt(result.processingFee)}` });
  }
  if (result.regulatoryFee > 0) {
    lines.push({ kind: 'calc',
      text: `Regulatory: ${fmt(result.regulatoryFee)} (digital services tax)` });
  }
  if (result.offsiteFee > 0) {
    lines.push({ kind: 'calc',
      text: `Offsite Ads: 15% × revenue (capped at ${sym}100) = ${fmt(result.offsiteFee)}` });
  }
  lines.push({ kind: 'subtotal', text: `Total fees: ${fmt(result.totalEtsyFees)}` });

  // Costs
  lines.push({ kind: 'header', text: 'Your costs' });
  if (result.productionCost > 0) {
    lines.push({ kind: 'calc', text: `Materials: ${fmt(result.productionCost)}` });
  }
  if (result.shippingCostPaid > 0) {
    lines.push({ kind: 'calc', text: `Shipping you pay: ${fmt(result.shippingCostPaid)}` });
  }
  if (result.packagingCost > 0) {
    lines.push({ kind: 'calc', text: `Packaging: ${fmt(result.packagingCost)}` });
  }
  if (result.laborCost > 0) {
    lines.push({ kind: 'calc', text: `Labor: ${fmt(result.laborCost)}` });
  }
  lines.push({ kind: 'subtotal', text: `Total costs + fees: ${fmt(result.totalCost)}` });

  // Profit
  lines.push({ kind: 'header', text: 'Profit' });
  lines.push({ kind: 'calc',
    text: `${fmt(result.revenue)} − ${fmt(result.totalCost)} = ${fmt(result.profit)}` });
  if (result.taxRate > 0 && result.profit > 0) {
    lines.push({ kind: 'calc',
      text: `Tax set-aside (${result.taxRate}%): ${fmt(result.profit)} × ${result.taxRate}% = ${fmt(result.taxAmount)}` });
    lines.push({ kind: 'subtotal',
      text: `You keep: ${fmt(result.afterTaxProfit)}` });
  }

  return (
    <div>
      {lines.map((l, i) => {
        if (l.kind === 'header') {
          return (
            <div key={i} style={{ marginTop: i === 0 ? 0 : '0.55rem', marginBottom: '0.2rem', fontWeight: 700, color: '#1e3a8a', fontSize: '0.86rem', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {l.text}
            </div>
          );
        }
        if (l.kind === 'subtotal') {
          return (
            <div key={i} style={{ paddingTop: '0.2rem', marginTop: '0.2rem', borderTop: '1px dashed rgba(26,26,46,0.2)', fontWeight: 700 }}>
              {l.text}
            </div>
          );
        }
        return <div key={i} style={{ paddingLeft: '0.5rem', color: 'rgba(26,26,46,0.75)' }}>{l.text}</div>;
      })}
    </div>
  );
}
